'use client'

/**
 * Pomodoro Timer Widget
 * =====================
 * Schwebender Timer für Ticket-Fokusblöcke.
 * Token-basiert — Dark + Light aus Theme-Tokens, keine gray-/purple-Hardcodes.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { BackendAuth } from '@/lib/auth-backend'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import BreakReminderSystem from './BreakReminderSystem'
import { useFocus } from '@/providers/focus-provider'
import { useTranslation } from '@/hooks/useTranslation'
import {
  Play,
  Pause,
  Square,
  Coffee,
  Brain,
  Users,
  Settings,
  Target,
  Heart,
  Minimize2,
  Volume2,
  VolumeX,
  RotateCcw,
  GripVertical,
} from 'lucide-react'

// Merkzettel fuer den Platz der Karte. Wer sie einmal beiseitegeschoben hat,
// will sie beim naechsten Vorgang nicht wieder wegschieben muessen.
const POS_KEY = 'xoffi.pomodoro.position'
// Merkzettel fuer einen laufenden Durchgang — ueberlebt Schliessen des
// Popovers, Seitenwechsel und F5.
const LAUF_KEY = 'xoffi.pomodoro.run'
// So lange nach Ablauf wird ein Durchgang noch nachtraeglich abgeschlossen.
// Weiter zurueck waere es Spuk: niemand will eine Pausenmeldung fuer einen
// Block, der vor zwei Stunden endete.
const NACHTRAG_FENSTER_SEK = 5 * 60
const RAND = 8 // Mindestabstand zum Fensterrand in Pixeln

export default function PomodoroTimer({
  currentTicketId,
  isMinimized = false,
  onToggleMinimize,
  className = '',
  ticketId,
  // Eingebettet = die Karte sitzt IN einem Behaelter (z.B. dem Uhr-Popover im
  // Header) statt frei ueber der Seite zu schweben. Dann gibt es weder festes
  // Positionieren noch Ziehgriff: der Behaelter bestimmt den Platz, und
  // geschlossen wird er ueber seinen eigenen Weg (Klick daneben / Escape).
  embedded = false,
}) {
  const { t } = useTranslation()
  const effectiveTicketId = currentTicketId || ticketId
  const [timeLeft, setTimeLeft] = useState(25 * 60)
  const [isActive, setIsActive] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [currentMode, setCurrentMode] = useState('work')
  const [sessionCount, setSessionCount] = useState(0)
  const [dailyStats, setDailyStats] = useState({
    completedPomodoros: 0,
    totalFocusTime: 0,
    ticketsWorked: 0,
    efficiency: 0,
  })

  const [settings, setSettings] = useState({
    workDuration: 25,
    shortBreakDuration: 5,
    longBreakDuration: 15,
    longBreakInterval: 4,
    soundEnabled: true,
    autoStartBreaks: true,
    autoStartPomodoros: false,
    showNotifications: true,
  })

  const [focusMode, setFocusMode] = useState('ticket')
  const [currentTask, setCurrentTask] = useState('')
  const [isDragging, setIsDragging] = useState(false)

  // ── VERSCHIEBEN DER KARTE ────────────────────────────────────────────
  // Bewusst EIGENE Pointer-Logik statt framer-motions `drag`. Die Gesten-
  // Engine startet seit Version 12 keinen Drag mehr, wenn der Druckpunkt auf
  // einem bedienbaren Kind liegt (BUTTON, INPUT, SELECT, TEXTAREA, A —
  // motion-dom/gestures/press/utils/is-keyboard-accessible). Diese Karte
  // besteht fast nur aus solchen Elementen: vier Modusknoepfe, Start, Stop,
  // Ton, das Eingabefeld. Dazu kommt ein globales Drag-Schloss (setDragLock),
  // das nach einer abgebrochenen Geste irgendwo sonst in der Oberflaeche
  // jeden weiteren Drag stillschweigend verweigert. Beides zusammen ergab
  // genau das gemeldete Verhalten: die Karte klebt oben rechts fest.
  //
  // Drei Zeilen Pointer-Events koennen dasselbe, ohne fremde Zustandsmaschine
  // — und mit einem ANFASSER, an dem man sieht, wo man greifen muss.
  const [pos, setPos] = useState(null) // null = Standardplatz oben rechts
  const kartenRef = useRef(null)
  const griffRef = useRef(null)

  const klemme = useCallback((x, y) => {
    // Die Karte darf nie so weit raus, dass ihr Anfasser unerreichbar wird —
    // sonst ist sie fuer immer weg und niemand weiss, warum.
    const b = kartenRef.current?.getBoundingClientRect()
    const breite = b?.width || 320
    const hoehe = b?.height || 200
    const maxX = Math.max(RAND, window.innerWidth - breite - RAND)
    const maxY = Math.max(RAND, window.innerHeight - hoehe - RAND)
    return {
      x: Math.min(Math.max(RAND, x), maxX),
      y: Math.min(Math.max(RAND, y), maxY),
    }
  }, [])

  useEffect(() => {
    try {
      const roh = window.localStorage.getItem(POS_KEY)
      if (!roh) return
      const p = JSON.parse(roh)
      if (Number.isFinite(p?.x) && Number.isFinite(p?.y)) setPos(klemme(p.x, p.y))
    } catch (_e) {
      /* Kein gemerkter Platz ist kein Fehler — dann eben oben rechts. */
    }
  }, [klemme])

  // Fenster kleiner gezogen? Dann die Karte zurueck ins Sichtbare holen.
  useEffect(() => {
    if (!pos) return
    const beiGroesse = () => setPos((alt) => (alt ? klemme(alt.x, alt.y) : alt))
    window.addEventListener('resize', beiGroesse)
    return () => window.removeEventListener('resize', beiGroesse)
  }, [pos, klemme])

  const ziehenStart = useCallback((e) => {
    if (e.button != null && e.button !== 0) return
    const b = kartenRef.current?.getBoundingClientRect()
    if (!b) return
    griffRef.current = { dx: e.clientX - b.left, dy: e.clientY - b.top }
    setIsDragging(true)
    // Zeiger einfangen: ohne das verliert die Karte den Kontakt, sobald der
    // Zeiger beim schnellen Ziehen ueber ein anderes Element huscht.
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (_e) {
      /* aeltere Browser koennen das nicht — dann eben ohne */
    }
    e.preventDefault()
  }, [])

  const ziehenBewegen = useCallback(
    (e) => {
      if (!griffRef.current) return
      setPos(klemme(e.clientX - griffRef.current.dx, e.clientY - griffRef.current.dy))
    },
    [klemme]
  )

  const ziehenEnde = useCallback((e) => {
    if (!griffRef.current) return
    griffRef.current = null
    setIsDragging(false)
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch (_e) {
      /* egal */
    }
  }, [])

  // Gemerkt wird erst NACH dem Loslassen — waehrend des Ziehens waeren das
  // dutzende Schreibvorgaenge pro Sekunde.
  useEffect(() => {
    if (isDragging || !pos) return
    try {
      window.localStorage.setItem(POS_KEY, JSON.stringify(pos))
    } catch (_e) {
      /* privater Modus o.ae. — dann merkt sich der Platz eben nichts */
    }
  }, [isDragging, pos])

  // Standardplatz oben rechts, sobald die Karte einmal bewegt wurde ueber
  // left/top. `right` MUSS dabei zurueckgesetzt werden, sonst zieht die
  // Klasse `right-4` die Karte weiter an den rechten Rand.
  const platzKlasse = embedded ? '' : pos ? 'fixed z-50' : 'fixed top-4 right-4 z-50'
  const platzStil = embedded
    ? undefined
    : pos
      ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' }
      : undefined

  // Der Anfasser. Ueberall sonst auf der Karte bleibt das normale Klicken —
  // wer den Timer startet, will ihn nicht verschieben.
  const griffProps = embedded
    ? {}
    : {
        onPointerDown: ziehenStart,
        onPointerMove: ziehenBewegen,
        onPointerUp: ziehenEnde,
        onPointerCancel: ziehenEnde,
        style: { cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' },
      }
  const [showBreakReminder, setShowBreakReminder] = useState(false)
  const [lastBreakTime, setLastBreakTime] = useState(null)
  const [totalWorkTime, setTotalWorkTime] = useState(0)

  const intervalRef = useRef(null)
  const audioRef = useRef(null)
  const statsSupportedRef = useRef(true)

  const focus = useFocus()
  const analyticsEnabled = Boolean(focus?.settings?.analytics?.enable_daily_stats)

  const focusModes = [
    {
      value: 'ticket',
      label: t('tickets.pomo.mode.ticket', 'Ticket'),
      icon: Target,
    },
    {
      value: 'meeting',
      label: t('tickets.pomo.mode.meeting', 'Meeting'),
      icon: Users,
    },
    {
      value: 'admin',
      label: t('tickets.pomo.mode.admin', 'Admin'),
      icon: Settings,
    },
    {
      value: 'learning',
      label: t('tickets.pomo.mode.learning', 'Lernen'),
      icon: Brain,
    },
  ]

  const timerModes = {
    work: {
      label: t('tickets.pomo.work', 'Fokuszeit'),
      color: 'text-destructive',
      icon: Brain,
    },
    shortBreak: {
      label: t('tickets.pomo.short', 'Kurze Pause'),
      color: 'text-[var(--color-success-accent,var(--color-green-600))]',
      icon: Coffee,
    },
    longBreak: {
      label: t('tickets.pomo.long', 'Lange Pause'),
      color: 'text-primary',
      icon: Heart,
    },
  }

  useEffect(() => {
    if (isActive && !isPaused) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((time) => {
          if (time <= 1) {
            handleTimerComplete()
            return 0
          }
          return time - 1
        })
      }, 1000)
    } else {
      clearInterval(intervalRef.current)
    }

    return () => clearInterval(intervalRef.current)
  }, [isActive, isPaused])

  useEffect(() => {
    if (!focus?.settings) return
    const s = focus.settings
    setSettings((prev) => ({
      ...prev,
      workDuration: Number(s?.pomodoro?.work_minutes) || prev.workDuration,
      shortBreakDuration: Number(s?.pomodoro?.short_break_minutes) || prev.shortBreakDuration,
      longBreakDuration: Number(s?.pomodoro?.long_break_minutes) || prev.longBreakDuration,
      longBreakInterval: Number(s?.pomodoro?.long_break_every) || prev.longBreakInterval,
    }))
  }, [focus?.settings])

  const resolvedDurations = React.useMemo(() => {
    const work = Number(settings.workDuration) || 25
    const shortBreak = Number(settings.shortBreakDuration) || 5
    const longBreak = Number(settings.longBreakDuration) || 15
    return { work, shortBreak, longBreak }
  }, [settings.workDuration, settings.shortBreakDuration, settings.longBreakDuration])

  useEffect(() => {
    if (isActive) return
    setTimeLeft(resolvedDurations.work * 60)
  }, [resolvedDurations.work, isActive])

  // Ein laufender Pomodoro darf NICHT sterben, nur weil die Karte kurz zu ist.
  // Seit der Timer im Uhr-Popover sitzt, wird er beim Schliessen abgebaut —
  // ohne diesen Merkzettel waere jeder Blick auf etwas anderes ein Neustart.
  // Gemerkt wird das ENDE als Zeitstempel, nicht die Restsekunden: nur so
  // stimmt die Zeit auch dann, wenn die Karte 10 Minuten zu war.
  useEffect(() => {
    try {
      const roh = window.localStorage.getItem(LAUF_KEY)
      if (!roh) return
      const l = JSON.parse(roh)
      if (!l?.active) return
      // Uralte Reste (Browser tagelang offen) nicht wiederbeleben.
      if (l.paused) {
        if (!Number.isFinite(l.restSec) || l.restSec <= 0) return
        setCurrentMode(l.mode || 'work')
        setIsActive(true)
        setIsPaused(true)
        setTimeLeft(l.restSec)
        return
      }
      const rest = Math.floor((Number(l.endsAt) - Date.now()) / 1000)
      if (!Number.isFinite(rest)) {
        window.localStorage.removeItem(LAUF_KEY)
        return
      }
      if (rest <= 0) {
        // Der Durchgang ist abgelaufen, waehrend die Karte zu war. Ist das
        // gerade erst passiert, wird er ORDENTLICH zu Ende gebracht (Ton,
        // Meldung, Zaehlung, Pause) — dafuer laeuft er mit einer Restsekunde
        // an und faellt gleich in den normalen Abschluss. handleTimerComplete
        // hier direkt zu rufen waere falsch: es liest currentMode, und der
        // ist in diesem Durchlauf noch der alte.
        const abgelaufenSeit = -rest
        window.localStorage.removeItem(LAUF_KEY)
        if (abgelaufenSeit <= NACHTRAG_FENSTER_SEK) {
          setCurrentMode(l.mode || 'work')
          setIsPaused(false)
          setIsActive(true)
          setTimeLeft(1)
        }
        return
      }
      setCurrentMode(l.mode || 'work')
      setIsActive(true)
      setIsPaused(false)
      setTimeLeft(rest)
    } catch (_e) {
      /* kein Merkzettel = frischer Timer, kein Fehler */
    }
    // Absichtlich nur beim Aufbauen — danach fuehrt der Timer selbst.
  }, [])

  // Schreiben nur bei Zustandswechseln (Start/Pause/Stop/Moduswechsel),
  // NICHT im Sekundentakt — sonst waeren das 3600 Schreibvorgaenge pro Stunde.
  useEffect(() => {
    try {
      if (!isActive) {
        window.localStorage.removeItem(LAUF_KEY)
        return
      }
      window.localStorage.setItem(
        LAUF_KEY,
        JSON.stringify(
          isPaused
            ? { active: true, paused: true, mode: currentMode, restSec: timeLeft }
            : { active: true, paused: false, mode: currentMode, endsAt: Date.now() + timeLeft * 1000 }
        )
      )
    } catch (_e) {
      /* privater Modus — dann eben ohne Merkzettel */
    }
    // timeLeft bewusst NICHT in den Abhaengigkeiten: sonst schriebe das jede Sekunde.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isPaused, currentMode])

  useEffect(() => {
    if (!focus?.activeCycle) return
    try {
      const startsAt = new Date(focus.activeCycle.starts_at).getTime()
      const endsAt = new Date(focus.activeCycle.ends_at).getTime()
      const now = Date.now()
      const remaining = Math.max(0, Math.floor((endsAt - Math.max(startsAt, now)) / 1000))
      const plannedWorkMinutes =
        Number(focus.activeCycle.work_minutes) || settings.workDuration
      const plannedBreakMinutes =
        Number(focus.activeCycle.break_minutes) || settings.shortBreakDuration
      setSettings((prev) => ({
        ...prev,
        workDuration: plannedWorkMinutes,
        shortBreakDuration: plannedBreakMinutes,
      }))
      setCurrentMode('work')
      setIsPaused(false)
      setIsActive(remaining > 0)
      setTimeLeft(remaining || plannedWorkMinutes * 60)
      if (!currentTask && (focus.activeCycle.ticket_id || effectiveTicketId)) {
        setCurrentTask(`#${focus.activeCycle.ticket_id || effectiveTicketId}`)
      }
    } catch {
      /* ignore malformed cycle payload */
    }
  }, [
    focus?.activeCycle,
    currentTask,
    effectiveTicketId,
    settings.shortBreakDuration,
    settings.workDuration,
  ])

  useEffect(() => {
    if (!analyticsEnabled || !statsSupportedRef.current) return
    loadDailyStats()
  }, [analyticsEnabled])

  const loadDailyStats = async () => {
    if (!analyticsEnabled || !statsSupportedRef.current) return
    try {
      const response = await BackendAuth.apiCallJson('/api/tickets/productivity/daily-stats')
      setDailyStats(response || dailyStats)
    } catch (_err) {
      statsSupportedRef.current = false
    }
  }

  const handleTimerComplete = async () => {
    setIsActive(false)

    if (settings.soundEnabled && audioRef.current) {
      audioRef.current.play()
    }

    if (settings.showNotifications && 'Notification' in window) {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        new Notification(`${timerModes[currentMode].label} beendet`, {
          body:
            currentMode === 'work'
              ? t('tickets.pomo.notify.break', {
                  defaultValue: 'Zeit für eine Pause.',
                  editable: false,
                })
              : t('tickets.pomo.notify.work', {
                  defaultValue: 'Bereit für die nächste Fokuszeit.',
                  editable: false,
                }),
        })
      }
    }

    await trackSession()

    if (currentMode === 'work') {
      const shouldTakeLongBreak = (sessionCount + 1) % settings.longBreakInterval === 0
      const nextMode = shouldTakeLongBreak ? 'longBreak' : 'shortBreak'

      setCurrentMode(nextMode)
      setTimeLeft(
        shouldTakeLongBreak
          ? resolvedDurations.longBreak * 60
          : resolvedDurations.shortBreak * 60
      )

      setShowBreakReminder(true)
      setTotalWorkTime((prev) => prev + resolvedDurations.work)

      if (settings.autoStartBreaks) {
        setIsActive(true)
      }
    } else {
      setCurrentMode('work')
      setTimeLeft(resolvedDurations.work * 60)

      if (currentMode === 'shortBreak' || currentMode === 'longBreak') {
        setSessionCount((prev) => prev + 1)
      }

      setLastBreakTime(new Date().toISOString())
      setShowBreakReminder(false)

      if (settings.autoStartPomodoros) {
        setIsActive(true)
      }
    }
  }

  const trackSession = async () => {
    try {
      const sessionData = {
        mode: currentMode,
        focus_mode: focusMode,
        duration_minutes:
          currentMode === 'work'
            ? resolvedDurations.work
            : currentMode === 'shortBreak'
              ? resolvedDurations.shortBreak
              : resolvedDurations.longBreak,
        ticket_id: focus?.activeCycle?.ticket_id || effectiveTicketId,
        task_description: currentTask,
        completed: true,
      }

      await BackendAuth.apiCallJson('/api/tickets/productivity/sessions', {
        method: 'POST',
        body: JSON.stringify(sessionData),
      })

      if (analyticsEnabled && statsSupportedRef.current) {
        loadDailyStats()
      }
    } catch (err) {
      console.error('Error tracking session:', err)
    }
  }

  const startTimer = () => {
    setIsActive(true)
    setIsPaused(false)
  }

  const pauseTimer = () => {
    setIsPaused(!isPaused)
  }

  const stopTimer = () => {
    setIsActive(false)
    setIsPaused(false)
    if (currentMode === 'work') {
      setTimeLeft(resolvedDurations.work * 60)
    } else if (currentMode === 'shortBreak') {
      setTimeLeft(resolvedDurations.shortBreak * 60)
    } else {
      setTimeLeft(resolvedDurations.longBreak * 60)
    }
    try {
      focus?.stopCycle?.()
    } catch {
      /* optional focus provider */
    }
  }

  const skipSession = () => {
    handleTimerComplete()
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const getProgress = () => {
    const totalDuration =
      currentMode === 'work'
        ? resolvedDurations.work * 60
        : currentMode === 'shortBreak'
          ? resolvedDurations.shortBreak * 60
          : resolvedDurations.longBreak * 60

    return ((totalDuration - timeLeft) / totalDuration) * 100
  }

  const currentModeConfig = timerModes[currentMode]
  const currentFocusMode = focusModes.find((fm) => fm.value === focusMode)
  const ModeIcon = currentModeConfig.icon
  const FocusIcon = currentFocusMode?.icon || Target

  if (isMinimized && !embedded) {
    return (
      <div ref={kartenRef} className={`${platzKlasse} ${className}`} style={platzStil}>
        {/* Im eingeklappten Zustand ist die ganze Pille der Anfasser: sie ist
            zu klein fuer einen eigenen Griff. Der Klick zum Aufklappen
            ueberlebt das, weil ein Klick ohne Bewegung kein Ziehen ist. */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          className="rounded-full border-2 border-border bg-card/95 p-3 shadow-lg backdrop-blur-sm"
          onClick={() => {
            if (!isDragging) onToggleMinimize?.()
          }}
          {...griffProps}
        >
          <div className="flex items-center gap-2">
            <div
              className={`h-3 w-3 rounded-full ${
                isActive ? 'animate-pulse bg-destructive' : 'bg-muted-foreground'
              }`}
            />
            <span className="font-mono text-sm font-bold text-foreground">
              {formatTime(timeLeft)}
            </span>
            <ModeIcon className={`h-4 w-4 ${currentModeConfig.color}`} />
          </div>
        </motion.div>
      </div>
    )
  }

  const Huelle = embedded ? 'div' : motion.div
  const huellenProps = embedded
    ? { className: className }
    : {
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 },
        className: `${platzKlasse} ${className}`,
        style: platzStil,
      }

  return (
    <Huelle ref={kartenRef} {...huellenProps}>
      <div
        className={
          embedded
            ? 'w-full'
            : 'w-80 rounded-xl border border-border bg-card/95 p-4 shadow-2xl backdrop-blur-sm'
        }
      >
        <div className="mb-4 flex items-center justify-between">
          {/* DER ANFASSER. Dass eine Karte verschiebbar ist, muss man SEHEN —
              vorher war die ganze Karte ziehbar, aber nichts sagte es, und
              zwischen Knoepfen und Eingabefeld traf man den ziehbaren Rest
              kaum. Jetzt ist der Titel der Griff: grosse Flaeche, eindeutig,
              und die Bedienelemente daneben bleiben anklickbar.
              Eingebettet entfaellt er — dort verschiebt man nichts. */}
          <div
            className={
              embedded
                ? 'flex flex-1 items-center gap-2'
                : '-m-1 flex flex-1 items-center gap-2 rounded p-1 hover:bg-accent/50'
            }
            title={
              embedded
                ? undefined
                : t('tickets.pomo.drag_hint', {
                    editable: false,
                    defaultValue: 'Zum Verschieben hier ziehen',
                  })
            }
            {...griffProps}
          >
            {!embedded && <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />}
            <ModeIcon className={`h-5 w-5 ${currentModeConfig.color}`} />
            <h3 className="font-semibold text-foreground">{currentModeConfig.label}</h3>
          </div>

          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setSettings((prev) => ({ ...prev, soundEnabled: !prev.soundEnabled }))
              }
              className="p-1"
            >
              {settings.soundEnabled ? (
                <Volume2 className="h-4 w-4" />
              ) : (
                <VolumeX className="h-4 w-4" />
              )}
            </Button>

            {onToggleMinimize && (
              <Button size="sm" variant="ghost" onClick={onToggleMinimize} className="p-1">
                <Minimize2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="mb-4 text-center">
          <div className="relative mx-auto mb-3 h-32 w-32">
            <svg className="h-full w-full -rotate-90 transform" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                className="text-border"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - getProgress() / 100)}`}
                className={`${currentModeConfig.color} transition-all duration-1000 ease-out`}
                strokeLinecap="round"
              />
            </svg>

            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-mono text-2xl font-bold text-foreground">
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>

          <Badge variant="secondary" appearance="light" className="mb-2">
            {t('tickets.pomo.session', {
              defaultValue: 'Session #{n}',
              editable: false,
            }).replace('{n}', String(sessionCount + 1))}
          </Badge>
        </div>

        <div className="mb-4">
          <div className="mb-2 flex items-center gap-2">
            <FocusIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {t('tickets.pomo.focus_mode', 'Fokus-Modus')}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {focusModes.map((mode) => {
              const ItemIcon = mode.icon
              const active = focusMode === mode.value
              return (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setFocusMode(mode.value)}
                  disabled={isActive}
                  className={`rounded-lg p-2 text-xs transition-all ${
                    active
                      ? 'border-2 border-primary bg-primary/10 text-foreground'
                      : 'border border-border bg-black/[0.04] text-muted-foreground hover:bg-accent dark:bg-white/[0.06]'
                  } ${isActive ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <ItemIcon className="mx-auto mb-1 h-3 w-3" />
                  <div>{mode.label}</div>
                </button>
              )
            })}
          </div>
        </div>

        {currentMode === 'work' && (
          <div className="mb-4">
            <Input
              type="text"
              placeholder={t('tickets.pomo.task_placeholder', {
                defaultValue: 'Was arbeitest du gerade?',
                editable: false,
              })}
              value={currentTask}
              onChange={(e) => setCurrentTask(e.target.value)}
              disabled={isActive}
              className="min-h-11 text-sm"
            />
          </div>
        )}

        <div className="mb-4 flex items-center justify-center gap-2">
          {!isActive ? (
            <Button onClick={startTimer} className="min-h-11">
              <Play className="mr-2 h-4 w-4" />
              {t('tickets.pomo.start', 'Start')}
            </Button>
          ) : (
            <Button onClick={pauseTimer} variant="outline" className="min-h-11">
              <Pause className="mr-2 h-4 w-4" />
              {isPaused
                ? t('tickets.pomo.resume', 'Fortsetzen')
                : t('tickets.pomo.pause', 'Pausieren')}
            </Button>
          )}

          <Button onClick={stopTimer} variant="outline" className="min-h-11">
            <Square className="mr-2 h-4 w-4" />
            {t('tickets.pomo.stop', 'Stop')}
          </Button>

          {isActive && (
            <Button onClick={skipSession} variant="outline" className="min-h-11">
              <RotateCcw className="mr-2 h-4 w-4" />
              {t('tickets.pomo.skip', 'Skip')}
            </Button>
          )}
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between text-sm">
            <div className="text-center">
              <div className="font-bold text-foreground">{dailyStats.completedPomodoros}</div>
              <div className="text-xs text-muted-foreground">
                {t('tickets.pomo.stat.pomos', 'Pomodoros')}
              </div>
            </div>
            <div className="text-center">
              <div className="font-bold text-foreground">
                {Math.round(dailyStats.totalFocusTime / 60)}h
              </div>
              <div className="text-xs text-muted-foreground">
                {t('tickets.pomo.stat.focus', 'Fokuszeit')}
              </div>
            </div>
            <div className="text-center">
              <div className="font-bold text-foreground">{dailyStats.ticketsWorked}</div>
              <div className="text-xs text-muted-foreground">
                {t('tickets.pomo.stat.tickets', 'Tickets')}
              </div>
            </div>
            <div className="text-center">
              <div className="font-bold text-foreground">{dailyStats.efficiency}%</div>
              <div className="text-xs text-muted-foreground">
                {t('tickets.pomo.stat.eff', 'Effizienz')}
              </div>
            </div>
          </div>
        </div>

        <audio
          ref={audioRef}
          preload="auto"
          src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvnQ="
        />
      </div>

      <BreakReminderSystem
        isVisible={showBreakReminder}
        onDismiss={() => setShowBreakReminder(false)}
        onTakeBreak={() => {
          setShowBreakReminder(false)
          if (isActive) {
            setIsActive(false)
            setIsPaused(true)
          }
        }}
        workTime={totalWorkTime}
        lastBreakTime={lastBreakTime}
      />
    </Huelle>
  )
}
